/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {
  forwardRef,
  isValidElement,
  MouseEvent,
  ReactNode
} from 'react';
import {makeStyles} from '@material-ui/core';
import Box from '@material-ui/core/Box';
import MenuItem from '@material-ui/core/MenuItem';
import classnames from 'classnames';
import Tooltip from '@ion/dls/src/components/tooltip/Tooltip';
import {useIsPhone} from '@ion/dls/src/hooks/useMedia';
import {MaterialDeleteIcon} from '../../icons/current/MaterialDeleteIcon';
import {SystemDeleteIcon} from '../../icons/SystemDeleteIcon';
import {dlsColor} from '../../theme/theme';
import useThemeContext from '../../theme/useThemeContext';
import {IconSize} from '../../types/types';
import {ButtonSpinner} from '../loading/ButtonSpinner';

export interface CellButtonMenuItemType {
  onClick: (e: MouseEvent<HTMLLIElement>) => void;
  icon?: ReactNode;
  label: string;
  disabled?: boolean;
  isLoading?: boolean;
  title?: string;
  customStyle?: string;
  direction?: 'row' | 'column';
  disableRipple?: boolean;
}

const useStyle = makeStyles(() => ({
  labelContainer : {
    position       : 'relative',
    display        : 'flex',
    justifyContent : 'center',
    alignItems     : 'center'
  },
  loader : {
    position  : 'absolute',
    margin    : 0,
    marginTop : '10px'
  },
  // the current theme's row menu item - the DLS menu's own type and colour,
  // not Material UI's ...
  currentItem : {
    '&&' : {
      color      : dlsColor('grey1'),
      fontFamily : 'Inter',
      fontSize   : 14,
      fontWeight : 500,
      lineHeight : '20px'
    },
    // its icon in the DLS menu's own colour, whatever colour it was given -
    // a fill or stroke an icon sets is outranked by this one ...
    '& $iconBox' : {
      color                             : dlsColor('grey1'),
      '& svg [fill]:not([fill="none"])' : {
        fill : dlsColor('grey1')
      },
      '& svg [stroke]:not([stroke="none"])' : {
        stroke : dlsColor('grey1')
      }
    }
  },
  iconBox : {}
}));

// the current theme's DLS menu icons for the legacy ones a row menu still
// passes - the delete bin, as the filter drawer's favorites menu shows it ...
const getCurrentIcon = (icon: ReactNode): ReactNode => (
  isValidElement(icon) && icon.type === SystemDeleteIcon
    ? <MaterialDeleteIcon color={dlsColor('grey1')} size={IconSize.medium} />
    : icon
);

/**
 * @CellMenuItem
 *
 * CellMenuItem component showing inside CellMenu
 *
 *    <CellMenuItem icon={<MapEditIcon />}
 *      onClick={editAsset}
 *      label={translate('admin-assets:grid.menu.editAsset')} />
 *    <CellMenuItem icon={<SystemDeleteIcon />}
 *      onClick={deleteAsset}
 *      label={translate('admin-assets:grid.menu.delete')} />
 *
 */
export const CellMenuItem = forwardRef<HTMLLIElement, CellButtonMenuItemType>(({
  onClick,
  icon,
  disabled,
  label,
  isLoading,
  title,
  customStyle,
  direction = 'row',
  disableRipple = false
}: CellButtonMenuItemType, ref) => {
  const isPhone     = useIsPhone();
  const classes     = useStyle();
  const {isCurrent} = useThemeContext();

  return (
    <Tooltip arrow placement={'bottom'}
      disableHoverListener={!title}
      title={<span>{title}</span>}>
      <MenuItem ref={ref}
        onClick={onClick}
        className={classnames(customStyle, {[classes.currentItem]: isCurrent})}
        disableRipple={disableRipple}
        disabled={disabled || isLoading}>
        <Box display='flex' flexDirection={direction} p={1}>
          {
            icon && (
              <Box className={classes.iconBox}
                display='flex'
                ml={isPhone ? 2 : 0}
                alignItems='center'>
                {isCurrent ? getCurrentIcon(icon) : icon}
              </Box>
            )
          }
          <Box display='flex' ml={1} alignItems='center'>
            <div className={classes.labelContainer}>
              {isLoading && <ButtonSpinner overrideClassName={classes.loader} />}
              {label}
            </div>
          </Box>
        </Box>
      </MenuItem>
    </Tooltip>
  );
});
